export interface LevelingGem {
  name: string;
  icon?: string;
  buyPrice: number;      // lv1 non-corrupted (divine)
  buyChaos: number;
  sellPrice: number;     // max level non-corrupted (divine)
  sellChaos: number;
  maxLevel: number;
  profit: number;
  profitPct: number;
  buyCount: number;
  sellCount: number;
}

// Exceptional support gems to exclude (they don't level normally)
const EXCEPTIONAL_GEMS = new Set([
  "Empower Support", "Enhance Support", "Enlighten Support",
  "Awakened Empower Support", "Awakened Enhance Support", "Awakened Enlighten Support",
  // All other exceptional supports (49 total per wiki)
  "Accelerating Support", "Ancestral Call Support", "Arrogance Support",
  "Artillery Ballista Support", "Awakened Brutality Support", "Awakened Cast On Critical Strike Support",
  "Awakened Cold Penetration Support", "Awakened Deadly Ailments Support",
  "Awakened Elemental Damage with Attacks Support", "Awakened Fire Penetration Support",
  "Awakened Fork Support", "Awakened Generosity Support", "Awakened Greater Multiple Projectiles Support",
  "Awakened Increased Area of Effect Support", "Awakened Lightning Penetration Support",
  "Awakened Melee Physical Damage Support", "Awakened Melee Splash Support",
  "Awakened Multistrike Support", "Awakened Spell Echo Support",
  "Awakened Unbound Ailments Support", "Awakened Vicious Projectiles Support",
  "Awakened Void Manipulation Support",
  "Barrage Support", "Bloodthirst Support", "Brutality Support",
  "Channelling Support", "Charged Traps Support", "Cluster Traps Support",
  "Cold to Fire Support", "Combustion Support", "Companionship Support",
  "Concentrated Effect Support", "Controlled Destruction Support",
  "Corrupting Fever Support", "Cruelty Support", "Culling Strike Support",
  "Cull the Weak Support", "Decay Support", "Devour Support",
  "Earthbreaker Support", "Efficient Training Support", "Eldritch Battery Support",
  "Energy Leech Support", "Eternal Blessing Support", "Forceful Skewering Support",
  "Fortify Support", "Frenzy Support", "Glacial Cascade Support",
  "Greater Spell Echo Support", "Greater Volley Support",
  "Impending Doom Support", "Infernal Legion Support", "Inspiration Support",
  "Intuitive Link Support", "Item Quantity Support", "Item Rarity Support",
  "Lacerate Support", "Last Resort Support", "Life Tap Support",
  "Meat Shield Support", "Mirage Archer Support", "Nightblade Support",
  "Overcharge Support", "Pinpoint Support", "Predator Support",
  "Pulverise Support", "Rage Support", "Shockwave Support",
  "Siege Ballista Support", "Sniper's Mark Support", "Spellblade Support",
  "Summon Phantasm Support", "Swift Affliction Support", "Swiftbrand Support",
  "Trauma Support", "Trinity Support", "Unleash Support",
  "Urgent Orders Support", "Void Manipulation Support", "Volatility Support",
  "Withering Touch Support",
]);

// Empower/Enhance/Enlighten max at level 3 (non-awakened)
const LEVEL3_GEMS = new Set(["Empower Support", "Enhance Support", "Enlighten Support"]);

export async function fetchLevelingGems(league = "Allflame"): Promise<LevelingGem[]> {
  const res = await fetch(`/api/gems?league=${encodeURIComponent(league)}`);
  const data = await res.json();
  const lines: any[] = data.lines ?? [];

  // Group by name, non-corrupted only
  const byName = new Map<string, any[]>();
  for (const raw of lines) {
    if (raw.corrupted) continue;
    if (EXCEPTIONAL_GEMS.has(raw.name)) continue;
    if (!byName.has(raw.name)) byName.set(raw.name, []);
    byName.get(raw.name)!.push(raw);
  }

  const results: LevelingGem[] = [];

  for (const [name, entries] of byName) {
    const maxLevel = LEVEL3_GEMS.has(name) ? 3 : 20;

    // Buy: lv1, q20
    const buyPool = entries.filter((e) => e.gemLevel === 1 && (e.gemQuality ?? 0) === 20);
    if (buyPool.length === 0) continue;
    const buyEntry = buyPool.reduce((a: any, b: any) => a.divineValue <= b.divineValue ? a : b);

    // Sell: max level, q20 preferred, non-corrupted
    const sellPoolQ20 = entries.filter((e) => e.gemLevel === maxLevel && (e.gemQuality ?? 0) === 20);
    const sellPoolAny = entries.filter((e) => e.gemLevel === maxLevel);
    const sellPool = sellPoolQ20.length > 0 ? sellPoolQ20 : sellPoolAny;
    if (sellPool.length === 0) continue;
    const sellEntry = sellPool.reduce((a: any, b: any) => a.divineValue <= b.divineValue ? a : b);

    const profit    = sellEntry.divineValue - buyEntry.divineValue;
    const profitPct = buyEntry.divineValue > 0 ? (profit / buyEntry.divineValue) * 100 : 0;

    results.push({
      name,
      icon: entries.find((e: any) => e.icon)?.icon,
      buyPrice:  buyEntry.divineValue ?? 0,
      buyChaos:  buyEntry.chaosValue ?? 0,
      sellPrice: sellEntry.divineValue ?? 0,
      sellChaos: sellEntry.chaosValue ?? 0,
      maxLevel,
      profit,
      profitPct,
      buyCount:  buyEntry.count ?? 0,
      sellCount: sellEntry.count ?? 0,
    });
  }

  results.sort((a, b) => b.profit - a.profit);
  return results;
}
